import React from 'react';

interface Profile {
  id?: number;
  first_name?: string;
  last_name?: string;
  phone?: string;
  zip?: string;
  city?: string;
  state?: string;
  street_address?: string;
  background?: string;
  avatar_url?: string;
  resume_file_name?: string;
  experiences?: any[];
  educations?: any[];
  skills?: any[];
  job_types?: any[];
  position_type_ids?: any[];
  profile_documents?: any[];
}

interface Badge {
  badge: string;
  earned: boolean;
}

interface ProfileStrengthProps {
  profile: Profile;
  badges: Badge[];
  recentApplicationsCount: number;
}

/**
 * Calculate profile completion percentage (same logic as ProfileCompleteness)
 */
function calculateCompletenessScore(profile: Profile): number {
  const fields = [
    { field: profile.first_name, value: 5 },
    { field: profile.last_name, value: 5 },
    { field: profile.phone, value: 5 },
    { field: profile.zip, value: 5 },
    { field: profile.city, value: 3 },
    { field: profile.state, value: 3 },
    { field: profile.street_address, value: 3 },
    { field: profile.background, value: 10 },
    { field: profile.avatar_url, value: 10 },
    { field: profile.resume_file_name, value: 15 },
    { field: profile.experiences, value: 15 },
    { field: profile.educations, value: 10 },
    { field: profile.skills, value: 15 },
    { field: profile.job_types, value: 10 },
    { field: profile.position_type_ids, value: 5 },
  ];

  const missingPoints = fields.reduce((acc, field) => {
    const isEmpty = Array.isArray(field.field)
      ? field.field.length === 0
      : !field.field || field.field === '';
    return isEmpty ? acc + field.value : acc;
  }, 0);

  return Math.max(0, 100 - missingPoints);
}

/**
 * ProfileStrength Component - Shows overall profile strength score
 * Factors: Completeness (40%), Badges (20%), Activity (20%), Skills (10%), Documents (10%)
 */
export const ProfileStrength: React.FC<ProfileStrengthProps> = ({
  profile,
  badges,
  recentApplicationsCount,
}) => {
  // Calculate individual scores
  const completenessScore = calculateCompletenessScore(profile);
  const completenessWeight = 40;

  // Badges score (20%)
  const earnedBadges = badges.filter(b => b.earned).length;
  const totalBadges = badges.length || 8; // Default to 8 possible badges
  const badgesScore = (earnedBadges / totalBadges) * 100;
  const badgesWeight = 20;

  // Activity score (20%) - based on recent applications
  const activityScore = Math.min((recentApplicationsCount / 5) * 100, 100);
  const activityWeight = 20;

  // Skills diversity score (10%)
  const skillsCount = Array.isArray(profile.skills) ? profile.skills.length : 0;
  const skillsScore = Math.min((skillsCount / 10) * 100, 100); // 10 skills = 100%
  const skillsWeight = 10;

  // Documents score (10%)
  const docsCount = Array.isArray(profile.profile_documents) ? profile.profile_documents.length : 0;
  const docsScore = Math.min((docsCount / 3) * 100, 100); // 3 docs = 100%
  const docsWeight = 10;

  // Calculate weighted total
  const totalScore = Math.round(
    (completenessScore * completenessWeight / 100) +
    (badgesScore * badgesWeight / 100) +
    (activityScore * activityWeight / 100) +
    (skillsScore * skillsWeight / 100) +
    (docsScore * docsWeight / 100)
  );

  const getScoreColor = (score: number) => {
    if (score >= 80) return { primary: '#71ad08', secondary: '#5e8f07' };
    if (score >= 60) return { primary: '#085ec8', secondary: '#176fc9' };
    return { primary: '#fe6718', secondary: '#ff8040' };
  };

  const colors = getScoreColor(totalScore);

  const breakdown = [
    { label: 'Profile Complete', score: completenessScore, weight: completenessWeight, icon: 'fa-user-check' },
    { label: 'Badges Earned', score: badgesScore, weight: badgesWeight, icon: 'fa-award' },
    { label: 'Recent Activity', score: activityScore, weight: activityWeight, icon: 'fa-chart-line' },
    { label: 'Skills Diversity', score: skillsScore, weight: skillsWeight, icon: 'fa-tools' },
    { label: 'Documents', score: docsScore, weight: docsWeight, icon: 'fa-file-alt' },
  ];

  return (
    <div className="content-card">
      <div className="content-card__header">
        <h3><i className="fas fa-chart-pie me-2"></i>Profile Strength</h3>
      </div>
      <div className="content-card__body">
        {/* Circular Progress */}
        <div className="d-flex justify-content-center mb-4">
          <div className="position-relative" style={{ width: '150px', height: '150px' }}>
            <svg className="position-absolute" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
              {/* Background circle */}
              <circle
                cx="75"
                cy="75"
                r="65"
                fill="none"
                stroke="#e2e8f0"
                strokeWidth="12"
              />
              {/* Progress circle */}
              <circle
                cx="75"
                cy="75"
                r="65"
                fill="none"
                stroke={`url(#gradient-${totalScore})`}
                strokeWidth="12"
                strokeDasharray={`${(totalScore / 100) * 408.407} 408.407`}
                strokeLinecap="round"
                style={{ transition: 'stroke-dasharray 1s ease' }}
              />
              <defs>
                <linearGradient id={`gradient-${totalScore}`} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={colors.primary} />
                  <stop offset="100%" stopColor={colors.secondary} />
                </linearGradient>
              </defs>
            </svg>
            <div
              className="position-absolute top-50 start-50 translate-middle text-center"
              style={{ width: '100%' }}
            >
              <div style={{ fontSize: '2.5rem', fontWeight: 700, color: colors.primary }}>
                {totalScore}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#6c757d', fontWeight: 600 }}>
                STRENGTH
              </div>
            </div>
          </div>
        </div>

        {/* Breakdown */}
        <div>
          <h6 className="text-muted mb-3" style={{ fontSize: '0.9rem', fontWeight: 600 }}>
            Score Breakdown
          </h6>
          {breakdown.map((item, index) => (
            <div key={index} className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span style={{ fontSize: '0.85rem', color: '#4a5568' }}>
                  <i className={`fas ${item.icon} me-2`} style={{ color: colors.primary, width: '16px' }}></i>
                  {item.label}
                </span>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: colors.primary }}>
                  {Math.round(item.score)}%
                </span>
              </div>
              <div
                style={{
                  height: '6px',
                  background: '#e2e8f0',
                  borderRadius: '3px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${item.score}%`,
                    height: '100%',
                    background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})`,
                    borderRadius: '3px',
                    transition: 'width 0.5s ease',
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Tips */}
        {totalScore < 80 && (
          <div
            className="mt-4 p-3 rounded"
            style={{ background: 'rgba(8, 94, 200, 0.05)', border: '1px solid rgba(8, 94, 200, 0.2)' }}
          >
            <h6 style={{ color: '#034ba6', fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.5rem' }}>
              <i className="fas fa-lightbulb me-2"></i>
              Boost Your Score
            </h6>
            <p style={{ fontSize: '0.85rem', color: '#4a5568', marginBottom: 0 }}>
              {totalScore < 60
                ? 'Complete your profile and add more skills to attract employers!'
                : 'Stay active by applying to jobs and earning badges!'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileStrength;
